// Module-level progress for revision modules — a session-scoped store that lets
// the slides of one module (which the lesson viewer renders independently, one
// at a time) share a running total and gate the Mark This Answer bonus.
//
// Deliberately self-contained: it lives in sessionStorage keyed by moduleId, and
// is read/written ONLY by the revision slide renderers. The shared lesson viewer
// is untouched, so non-revision lessons carry no risk. See
// REVISION_TEMPLATE_PLAN.md §0 (module-level chrome) + §10.

export interface ModuleProgress {
  earned: number;
  total: number;
  /** 0–100, rounded. 0 when nothing recorded yet. */
  pct: number;
  /** Accumulated time-on-task across recorded slides. */
  timeMs: number;
  /** How many source slides have reported in. */
  slideCount: number;
}

interface SlideRecord {
  earned: number;
  total: number;
  timeMs: number;
}

const KEY = (moduleId: string) => `kula-revmod-${moduleId}`;

function readRaw(moduleId: string): Record<string, SlideRecord> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(KEY(moduleId));
    return raw ? (JSON.parse(raw) as Record<string, SlideRecord>) : {};
  } catch {
    return {};
  }
}

/**
 * Record one source slide's contribution to its module. Idempotent per slideId —
 * re-recording the same slide (e.g. a retry) overwrites, never double-counts.
 */
export function recordSlideScore(
  moduleId: string,
  slideId: string,
  earned: number,
  total: number,
  timeMs = 0,
): void {
  if (typeof window === 'undefined' || !moduleId || !slideId) return;
  try {
    const records = readRaw(moduleId);
    records[slideId] = { earned, total, timeMs };
    window.sessionStorage.setItem(KEY(moduleId), JSON.stringify(records));
  } catch {
    /* storage full / unavailable — degrade silently (gate just stays open) */
  }
}

/** Aggregate a module's recorded slides. */
export function getModuleProgress(moduleId: string): ModuleProgress {
  const records = Object.values(readRaw(moduleId));
  const earned = records.reduce((n, r) => n + (r.earned || 0), 0);
  const total = records.reduce((n, r) => n + (r.total || 0), 0);
  const timeMs = records.reduce((n, r) => n + (r.timeMs || 0), 0);
  return {
    earned,
    total,
    pct: total > 0 ? Math.round((earned / total) * 100) : 0,
    timeMs,
    slideCount: records.length,
  };
}

/** Pass mark for the Mark This Answer bonus unlock. */
export const MODULE_UNLOCK_PCT = 70;
